// API Configuration for website-service
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3009/api";

export const api = {
  // CMS endpoints
  getPageBySlug: async (slug) => {
    try {
      const response = await fetch(`${API_BASE_URL}/pages/${slug}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to fetch page: ${response.status}`,
        );
      }
      const data = await response.json();
      if (data.success && data.data) {
        // Backend now returns normalized camelCase, but handle both cases for safety
        const page = data.data;
        return {
          id: page.id,
          title: page.title,
          slug: page.slug,
          content: page.content,
          contentType:
            page.contentType || page.contenttype || page.content_type,
          category: page.category,
          status: page.status,
          metaTitle: page.metaTitle || page.metatitle || page.meta_title,
          metaDescription:
            page.metaDescription ||
            page.metadescription ||
            page.meta_description,
          metaKeywords:
            page.metaKeywords || page.metakeywords || page.meta_keywords,
          sections: page.sections,
          isPublished:
            page.isPublished !== undefined
              ? page.isPublished
              : page.ispublished !== undefined
                ? page.ispublished
                : page.is_published !== undefined
                  ? page.is_published
                  : true,
          createdBy: page.createdBy || page.createdby || page.created_by,
          updatedBy: page.updatedBy || page.updatedby || page.updated_by,
          publishedAt:
            page.publishedAt || page.publishedat || page.published_at,
          createdAt: page.createdAt || page.createdat || page.created_at,
          updatedAt: page.updatedAt || page.updatedat || page.updated_at,
        };
      }
      throw new Error(data.error || "Failed to fetch page");
    } catch (error) {
      console.error(`Error fetching page ${slug}:`, error);
      throw error;
    }
  },

  getPages: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/pages`);
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(data.error || "Failed to fetch pages");
    } catch (error) {
      console.error("Error fetching pages:", error);
      throw error;
    }
  },

  getBanners: async (position) => {
    try {
      const url = position
        ? `${API_BASE_URL}/banners?position=${encodeURIComponent(position)}`
        : `${API_BASE_URL}/banners`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Ensure we return an array even if data.data is undefined
        return Array.isArray(data.data) ? data.data : [];
      }
      // If success is false, return empty array instead of throwing
      console.warn(
        "Banners API returned success: false",
        data.error || data.message,
      );
      return [];
    } catch (error) {
      console.error("Error fetching banners:", error);
      // Return empty array instead of throwing to prevent infinite loading
      return [];
    }
  },

  // CMS Page Config endpoints (for Mamangam Hall Booking, Advertise with Us, etc.)
  getPageConfigByKey: async (pageKey) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/cms/page-configs/${encodeURIComponent(pageKey)}`,
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to fetch page config: ${response.status}`,
        );
      }
      const data = await response.json();
      if (data.success && data.data) {
        return data.data;
      }
      throw new Error(data.error || "Failed to fetch page config");
    } catch (error) {
      console.error(`Error fetching page config ${pageKey}:`, error);
      throw error;
    }
  },

  getPageSections: async (pageKey) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/cms/page-configs/${encodeURIComponent(pageKey)}/sections`,
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to fetch page sections: ${response.status}`,
        );
      }
      const data = await response.json();
      if (data.success && data.data) {
        return Array.isArray(data.data) ? data.data : [];
      }
      return [];
    } catch (error) {
      console.error(`Error fetching page sections ${pageKey}:`, error);
      return [];
    }
  },

  // Movie endpoints
  getMovies: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.genre) params.append("genre", filters.genre);
      if (filters.language) params.append("language", filters.language);
      if (filters.status) params.append("status", filters.status);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.page) params.append("page", filters.page);

      const url = `${API_BASE_URL}/movies${params.toString() ? "?" + params.toString() : ""}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        return Array.isArray(data.data) ? data.data : [];
      }
      throw new Error(data.error || "Failed to fetch movies");
    } catch (error) {
      console.error("Error fetching movies:", error);
      throw error;
    }
  },

  getMovieById: async (movieId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/movies/${movieId}`);
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(data.error || "Failed to fetch movie");
    } catch (error) {
      console.error("Error fetching movie:", error);
      throw error;
    }
  },

  // City-aware Now Showing: returns master movies currently playing in cinemas
  // within ~25km of the user's selected city. Falls back to global catalogue on
  // the backend if no location is provided.
  getNowShowingByLocation: async ({ latitude, longitude, maxDistance, limit = 50 } = {}) => {
    try {
      const params = new URLSearchParams();
      if (latitude != null) params.append("latitude", String(latitude));
      if (longitude != null) params.append("longitude", String(longitude));
      if (maxDistance != null) params.append("maxDistance", String(maxDistance));
      params.append("limit", String(limit));

      const url = `${API_BASE_URL}/movies/now-showing?${params.toString()}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        return data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching now showing by location:", error);
      return [];
    }
  },

  // Cities endpoints (used by LocationDropdown for the city picker)
  getCities: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/cities`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        return data.data.map((city) => ({
          id: city.id,
          name: city.name,
          state: city.state,
          lat: city.latitude != null ? Number(city.latitude) : null,
          lng: city.longitude != null ? Number(city.longitude) : null,
          isActive: city.isActive,
          displayOrder: city.displayOrder,
          available: city.isActive !== false,
        }));
      }
      console.warn(
        "Cities API returned unexpected shape:",
        data.error || data.message,
      );
      return [];
    } catch (error) {
      console.error("Error fetching cities:", error);
      return [];
    }
  },

  searchCities: async (query, limit = 8) => {
    try {
      const url = `${API_BASE_URL}/cities/search?q=${encodeURIComponent(query)}&limit=${limit}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        return data.data.map((city) => ({
          id: city.id,
          name: city.name,
          state: city.state,
          lat: city.latitude != null ? Number(city.latitude) : null,
          lng: city.longitude != null ? Number(city.longitude) : null,
          isActive: city.isActive,
          displayOrder: city.displayOrder,
          available: city.isActive !== false,
        }));
      }
      return [];
    } catch (error) {
      console.error("Error searching cities:", error);
      return [];
    }
  },

  // Cinema endpoints
  getCinemas: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.city) params.append("city", filters.city);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.page) params.append("page", filters.page);
      // Add location parameters if provided
      if (filters.latitude !== undefined && filters.latitude !== null) {
        params.append("latitude", filters.latitude.toString());
      }
      if (filters.longitude !== undefined && filters.longitude !== null) {
        params.append("longitude", filters.longitude.toString());
      }
      if (filters.maxDistance !== undefined && filters.maxDistance !== null) {
        params.append("maxDistance", filters.maxDistance.toString());
      }
      // Empty-state fallback: bypass radius filter + pagination cap
      if (filters.unlimited) params.append("unlimited", "true");

      const url = `${API_BASE_URL}/cinemas${params.toString() ? "?" + params.toString() : ""}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        return Array.isArray(data.data) ? data.data : [];
      }
      throw new Error(data.error || "Failed to fetch cinemas");
    } catch (error) {
      console.error("Error fetching cinemas:", error);
      throw error;
    }
  },

  getCinemaById: async (cinemaId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cinemas/${cinemaId}`);
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(data.error || "Failed to fetch cinema");
    } catch (error) {
      console.error("Error fetching cinema:", error);
      throw error;
    }
  },

  getCinemaSchedule: async (cinemaId, date) => {
    if (!cinemaId) {
      throw new Error("cinemaId is required to fetch schedule");
    }

    try {
      const params = new URLSearchParams();
      if (date) {
        params.append("date", date);
      }

      const url = `${API_BASE_URL}/cinemas/${cinemaId}/schedule${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        return data.data;
      }

      throw new Error(data.error || "Failed to fetch cinema schedule");
    } catch (error) {
      console.error(`Error fetching schedule for cinema ${cinemaId}:`, error);
      throw error;
    }
  },

  // Show endpoints
  getShows: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.cinemaId) params.append("cinemaId", filters.cinemaId);
      if (filters.movieId) params.append("movieId", filters.movieId);
      if (filters.date) params.append("date", filters.date);

      const url = `${API_BASE_URL}/shows${params.toString() ? "?" + params.toString() : ""}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        return Array.isArray(data.data) ? data.data : [];
      }
      throw new Error(data.error || "Failed to fetch shows");
    } catch (error) {
      console.error("Error fetching shows:", error);
      throw error;
    }
  },

  getVistaShows: async (filmCode, date, cinemaId, locationParams = null) => {
    if (!filmCode) {
      return [];
    }

    try {
      const params = new URLSearchParams();
      params.append("filmCode", filmCode);
      if (date) {
        params.append("date", date);
      }
      if (cinemaId) {
        params.append("cinemaId", cinemaId);
      }
      // Add location parameters if provided
      if (
        locationParams &&
        locationParams.latitude !== undefined &&
        locationParams.latitude !== null
      ) {
        params.append("latitude", locationParams.latitude.toString());
      }
      if (
        locationParams &&
        locationParams.longitude !== undefined &&
        locationParams.longitude !== null
      ) {
        params.append("longitude", locationParams.longitude.toString());
      }
      if (
        locationParams &&
        locationParams.maxDistance !== undefined &&
        locationParams.maxDistance !== null
      ) {
        params.append("maxDistance", locationParams.maxDistance.toString());
      }

      const response = await fetch(
        `${API_BASE_URL}/shows/vista?${params.toString()}`,
      );
      const data = await response.json();
      if (data.success) {
        return Array.isArray(data.data) ? data.data : [];
      }
      throw new Error(data.error || "Failed to fetch Vista shows");
    } catch (error) {
      console.error("Error fetching Vista shows:", error);
      throw error;
    }
  },

  getSeatLayout: async (cinemaId, screenId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/shows/seat-layout?cinemaId=${cinemaId}&screenId=${screenId}`,
      );
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(data.error || "Failed to fetch seat layout");
    } catch (error) {
      console.error("Error fetching seat layout:", error);
      throw error;
    }
  },

  getVistaSeatLayout: async (cinemaId, sessionId, transId) => {
    try {
      if (!transId) {
        throw new Error("Transaction ID is required for Vista seat layout");
      }
      const response = await fetch(
        `${API_BASE_URL}/shows/seat-layout/vista/${encodeURIComponent(cinemaId)}/${encodeURIComponent(sessionId)}/${encodeURIComponent(transId)}`,
      );
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(data.error || "Failed to fetch Vista seat layout");
    } catch (error) {
      console.error("Error fetching Vista seat layout:", error);
      throw error;
    }
  },

  // Initialize Vista booking
  initVistaBooking: async (cinemaId, sessionId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/vista/init/${encodeURIComponent(cinemaId)}/${encodeURIComponent(sessionId)}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        },
      );
      const data = await response.json();
      if (data.success) {
        return data.data; // Returns { transactionId, cinemaId, sessionId }
      }
      throw new Error(data.error || "Failed to initialize Vista booking");
    } catch (error) {
      console.error("Error initializing Vista booking:", error);
      throw error;
    }
  },

  // Reinitialize Vista booking (cancel existing if provided, then create new)
  reinitVistaBooking: async (cinemaId, sessionId, existingTransId = null) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/vista/reinit/${encodeURIComponent(cinemaId)}/${encodeURIComponent(sessionId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            existingTransId: existingTransId || undefined,
          }),
        },
      );
      const data = await response.json();
      if (data.success) {
        return data.data; // Returns { transactionId, cinemaId, sessionId, cancelledExistingTransaction }
      }
      throw new Error(data.error || "Failed to reinitialize Vista booking");
    } catch (error) {
      console.error("Error reinitializing Vista booking:", error);
      throw error;
    }
  },

  // Add seats to Vista booking
  addSeatsToVistaBooking: async (
    cinemaId,
    sessionId,
    transId,
    seatType,
    quantity,
    userSelectedSeating = true,
  ) => {
    try {
      if (!transId || !String(transId).trim()) {
        throw new Error("Transaction ID is required and cannot be empty");
      }

      const url = `${API_BASE_URL}/bookings/vista/add-seats/${encodeURIComponent(cinemaId)}/${encodeURIComponent(sessionId)}/${encodeURIComponent(transId)}`;

      // console.log('Calling add-seats API:', {
      //   url,
      //   cinemaId,
      //   sessionId,
      //   transId,
      //   seatType,
      //   quantity,
      //   userSelectedSeating,
      // });

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          seatType: seatType,
          quantity: quantity,
          userSelectedSeating: userSelectedSeating,
        }),
      });

      const data = await response.json();

      // console.log('Add-seats API response:', {
      //   status: response.status,
      //   success: data.success,
      //   data: data.data,
      //   error: data.error,
      //   message: data.message,
      // });

      if (!response.ok || !data.success) {
        const errorMsg =
          data.message ||
          data.error ||
          `HTTP ${response.status}: ${response.statusText}`;
        const error = new Error(errorMsg);
        error.response = data;
        error.status = response.status;
        throw error;
      }

      return data.data;
    } catch (error) {
      console.error("Error adding seats to Vista booking:", {
        error: error.message,
        response: error.response,
        status: error.status,
        cinemaId,
        sessionId,
        transId,
      });
      throw error;
    }
  },

  // Continue Vista booking (proceed from snacks to payment)
  continueVistaBooking: async (cinemaId, transId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/vista/continue/${encodeURIComponent(cinemaId)}/${encodeURIComponent(transId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(
        data.error || data.message || "Failed to continue Vista booking",
      );
    } catch (error) {
      console.error("Error continuing Vista booking:", error);
      throw error;
    }
  },

  // Add concessions to Vista booking
  addConcessionsToVistaBooking: async (cinemaId, transId, items) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/vista/concessions/${encodeURIComponent(cinemaId)}/${encodeURIComponent(transId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: items,
          }),
        },
      );
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(
        data.error ||
          data.message ||
          "Failed to add concessions to Vista booking",
      );
    } catch (error) {
      console.error("Error adding concessions to Vista booking:", error);
      throw error;
    }
  },

  // Add booking fee to Vista booking
  // Coupon APIs
  validateCoupon: async (
    code,
    ticketPrice,
    snacksPrice = 0,
    convenienceFeeBase = 0,
    userId = null,
  ) => {
    try {
      const response = await fetch(`${API_BASE_URL}/coupons/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code,
          ticketPrice,
          snacksPrice,
          convenienceFeeBase,
          userId,
        }),
      });
      const data = await response.json();
      if (data.success) {
        return data;
      }
      throw new Error(data.error || "Failed to validate coupon");
    } catch (error) {
      console.error("Error validating coupon:", error);
      throw error;
    }
  },

  applyCoupon: async (
    couponId,
    bookingId,
    userId,
    discountAmount,
    orderValue,
  ) => {
    try {
      const response = await fetch(`${API_BASE_URL}/coupons/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          couponId,
          bookingId,
          userId,
          discountAmount,
          orderValue,
        }),
      });
      const data = await response.json();
      if (data.success) {
        return data;
      }
      throw new Error(data.error || "Failed to apply coupon");
    } catch (error) {
      console.error("Error applying coupon:", error);
      throw error;
    }
  },

  addBookingFeeToVistaBooking: async (cinemaId, transId, amount, quantity) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/vista/fee/${encodeURIComponent(cinemaId)}/${encodeURIComponent(transId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            amount: amount,
            quantity: quantity,
          }),
        },
      );
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(
        data.error ||
          data.message ||
          "Failed to add booking fee to Vista booking",
      );
    } catch (error) {
      console.error("Error adding booking fee to Vista booking:", error);
      throw error;
    }
  },

  // Set seats in Vista booking
  setSeatsInVistaBooking: async (cinemaId, sessionId, transId, seats) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/vista/set-seats/${encodeURIComponent(cinemaId)}/${encodeURIComponent(sessionId)}/${encodeURIComponent(transId)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            seats: seats,
          }),
        },
      );
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(
        data.error || data.message || "Failed to set seats in Vista booking",
      );
    } catch (error) {
      console.error("Error setting seats in Vista booking:", error);
      throw error;
    }
  },

  // Contact endpoints
  submitContact: async (formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contact/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (data.success) {
        return data;
      }
      const error = new Error(data.error || "Failed to submit contact form");
      error.response = data;
      throw error;
    } catch (error) {
      console.error("Error submitting contact form:", error);
      throw error;
    }
  },

  // User endpoints
  checkUserByMobile: async (mobile) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/check-mobile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ mobile }),
      });
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(data.error || "Failed to check user");
    } catch (error) {
      console.error("Error checking user by mobile:", error);
      throw error;
    }
  },

  registerUser: async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      const error = new Error(data.error || "Failed to register user");
      error.response = data;
      throw error;
    } catch (error) {
      console.error("Error registering user:", error);
      throw error;
    }
  },

  // Update user profile
  updateUserProfile: async (userId, profileData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to update user profile",
        );
      }

      if (data.success) {
        return data.data;
      }

      throw new Error(data.error || "Failed to update user profile");
    } catch (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }
  },

  deleteUserAccount: async (userId) => {
    try {
      if (!userId) {
        console.error("deleteUserAccount called with invalid userId:", userId);
        throw new Error("User ID is missing. Please re-login.");
      }
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to delete user account",
        );
      }

      if (data.success) {
        return data;
      }

      throw new Error(data.error || "Failed to delete user account");
    } catch (error) {
      console.error("Error deleting user account:", error);
      throw error;
    }
  },

  // OTP endpoints
  sendOTP: async (mobile, flow = "login", payload = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobile,
          flow,
          ...payload, // ✅ email, name for register
        }),
      });

      const data = await response.json();
      if (data.success) return data.data;
      throw new Error(data.error || "Failed to send OTP");
    } catch (err) {
      throw err;
    }
  },

  verifyOTP: async (mobile, otp, payload = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mobile,
          otp,
          ...payload, // 👈 MUST include flow + registration fields for register
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return {
          verified: false,
          error: data.error || data.message || "OTP verification failed",
        };
      }

      return {
        verified: true,
        user: data.data.user,
      };
    } catch (error) {
      console.error("verifyOTP error:", error);
      throw error;
    }
  },

  resendOTP: async (mobile, flow = "login") => {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/resend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mobile,
          flow, // keep flow consistent
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || "Failed to resend OTP");
      }

      return data.data;
    } catch (error) {
      console.error("resendOTP error:", error);
      throw error;
    }
  },

  // Email OTP for profile editing
  sendEmailOTP: async (email, userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          userId,
          flow: "profile-edit",
        }),
      });

      const data = await response.json();
      if (data.success) return data.data;
      throw new Error(data.error || "Failed to send email OTP");
    } catch (err) {
      throw err;
    }
  },

  verifyEmailOTP: async (email, otp, userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/verify-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          otp,
          userId,
          flow: "profile-edit",
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return {
          verified: false,
          error: data.error || data.message || "Email OTP verification failed",
        };
      }

      return {
        verified: true,
        token: data.data.token,
      };
    } catch (error) {
      console.error("verifyEmailOTP error:", error);
      throw error;
    }
  },

  // Booking endpoints
  createBooking: async (bookingData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingData),
      });
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      const error = new Error(data.error || "Failed to create booking");
      error.response = data;
      throw error;
    } catch (error) {
      console.error("Error creating booking:", error);
      throw error;
    }
  },

  createPendingBooking: async (bookingData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/pending`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingData),
      });
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      const error = new Error(data.error || "Failed to create pending booking");
      error.response = data;
      throw error;
    } catch (error) {
      console.error("Error creating pending booking:", error);
      throw error;
    }
  },

  // Easebuzz payment methods
  initiateEasebuzzPayment: async (paymentData) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/payments/easebuzz/initiate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(paymentData),
        },
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.message || data.error || "Failed to initiate Easebuzz payment",
        );
      }
      return data;
    } catch (error) {
      console.error("Error initiating Easebuzz payment:", error);
      throw error;
    }
  },

  confirmBooking: async (bookingId, paymentData) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/${bookingId}/confirm`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(paymentData),
        },
      );
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      const error = new Error(data.error || "Failed to confirm booking");
      error.response = data;
      throw error;
    } catch (error) {
      console.error("Error confirming booking:", error);
      throw error;
    }
  },

  getUserBookings: async (userId, page = 1, limit = 10) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/user/${userId}?page=${page}&limit=${limit}`,
      );
      const data = await response.json();
      if (data.success) {
        // Handle both new format (with pagination) and old format (just array)
        if (Array.isArray(data.data)) {
          // Old format - return as array with default pagination
          return {
            bookings: data.data || [],
            pagination: data.pagination || {
              page: page,
              limit: limit,
              total: data.data?.length || 0,
              totalPages: 1,
              hasNextPage: false,
              hasPreviousPage: false,
            },
          };
        }
        // New format with pagination
        return {
          bookings: data.data || [],
          pagination: data.pagination || {
            page: page,
            limit: limit,
            total: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        };
      }
      throw new Error(data.error || "Failed to fetch bookings");
    } catch (error) {
      console.error("Error fetching user bookings:", error);
      throw error;
    }
  },

  getGuestBookings: async (email, phone, page = 1, limit = 10) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/guest?email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}&page=${page}&limit=${limit}`,
      );
      const data = await response.json();
      if (data.success) {
        // Handle both new format (with pagination) and old format (just array)
        if (Array.isArray(data.data)) {
          // Old format - return as array with default pagination
          return {
            bookings: data.data || [],
            pagination: data.pagination || {
              page: page,
              limit: limit,
              total: data.data?.length || 0,
              totalPages: 1,
              hasNextPage: false,
              hasPreviousPage: false,
            },
          };
        }
        // New format with pagination
        return {
          bookings: data.data || [],
          pagination: data.pagination || {
            page: page,
            limit: limit,
            total: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        };
      }
      throw new Error(data.error || "Failed to fetch guest bookings");
    } catch (error) {
      console.error("Error fetching guest bookings:", error);
      throw error;
    }
  },

  cancelBooking: async (bookingId, userId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/${bookingId}/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        // Handle HTTP errors (4xx, 5xx)
        throw new Error(
          data.message ||
            data.error ||
            `Failed to cancel booking (${response.status})`,
        );
      }

      if (data.success) {
        return data;
      }

      throw new Error(data.message || data.error || "Failed to cancel booking");
    } catch (error) {
      console.error("Error cancelling booking:", error);
      throw error;
    }
  },

  refundBooking: async (bookingId, payload) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/${bookingId}/refund`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload), // 👈 contains userId, reason, refundAmount
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            data.error ||
            `Failed to refund booking (${response.status})`,
        );
      }

      if (data.success) {
        return data;
      }

      throw new Error(data.message || data.error || "Failed to refund booking");
    } catch (error) {
      console.error("Error refunding booking:", error);
      throw error;
    }
  },

  getBookingById: async (bookingId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}`);
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(data.error || "Failed to fetch booking");
    } catch (error) {
      console.error("Error fetching booking:", error);
      throw error;
    }
  },

  getBookingByPaymentToken: async (token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/payment-token/${token}`,
      );
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(data.error || "Failed to fetch booking");
    } catch (error) {
      console.error("Error fetching booking by payment token:", error);
      throw error;
    }
  },

  // User endpoints
  getUserById: async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`);
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(data.error || "Failed to fetch user");
    } catch (error) {
      console.error("Error fetching user:", error);
      throw error;
    }
  },

  // Settings endpoints
  getSettings: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings`);
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      throw new Error(data.error || "Failed to fetch settings");
    } catch (error) {
      console.error("Error fetching settings:", error);
      throw error;
    }
  },

  // Snacks/Concessions endpoints
  getSnacks: async (cinemaId, isVistaId = false) => {
    if (!cinemaId) {
      throw new Error("cinemaId is required to fetch snacks");
    }
    try {
      // If isVistaId is true, pass it as vistaCinemaId parameter
      const url = isVistaId
        ? `${API_BASE_URL}/snacks?vistaCinemaId=${encodeURIComponent(cinemaId)}`
        : `${API_BASE_URL}/snacks?cinemaId=${encodeURIComponent(cinemaId)}`;

      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        return Array.isArray(data.data) ? data.data : [];
      }
      throw new Error(data.error || "Failed to fetch snacks");
    } catch (error) {
      console.error("Error fetching snacks:", error);
      throw error;
    }
  },

  // Booking view token endpoints (for secure direct URL access)
  getBookingByViewToken: async (token) => {
    if (!token) {
      throw new Error("View token is required");
    }
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/view/${encodeURIComponent(token)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to fetch booking",
        );
      }
      return data;
    } catch (error) {
      console.error("Error fetching booking by view token:", error);
      throw error;
    }
  },

  // Short link → get original booking URL
  getShortLink: async (code) => {
    if (!code) {
      throw new Error("Short code is required");
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/short-link/${encodeURIComponent(code)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to fetch short link",
        );
      }

      return data;
    } catch (error) {
      console.error("Error fetching short link:", error);
      throw error;
    }
  },

  cancelBookingByViewToken: async (token, reason) => {
    if (!token) {
      throw new Error("View token is required");
    }
    try {
      const response = await fetch(
        `${API_BASE_URL}/bookings/view/${encodeURIComponent(token)}/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason: reason || null }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to cancel booking",
        );
      }
      return data;
    } catch (error) {
      console.error("Error cancelling booking by view token:", error);
      throw error;
    }
  },

  // Offers endpoints
  getOffers: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/offers`);
      const data = await response.json();

      if (data.success) {
        return Array.isArray(data.data) ? data.data : [];
      }

      return [];
    } catch (error) {
      console.error("Error fetching offers:", error);
      return [];
    }
  },

  getHomeOffers: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/offers/home`);
      const data = await response.json();

      if (data.success) {
        return Array.isArray(data.data) ? data.data : [];
      }

      return [];
    } catch (error) {
      console.error("Error fetching home offers:", error);
      return [];
    }
  },

  // Launch Offer status — used to show "Free 35g Popcorn" banner + line item
  // on Payment page and other surfaces during the active period.
  getLaunchOfferStatus: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/launch-offer/status`);
      const data = await response.json();
      if (data.success && data.data) {
        return {
          active: !!data.data.active,
          offerTitle: data.data.offerTitle || "Website Launch Offer",
          itemName: data.data.itemName || "Complimentary 35g Popcorn",
          endDate: data.data.endDate || null,
        };
      }
      return { active: false, offerTitle: "Website Launch Offer", itemName: "Complimentary 35g Popcorn", endDate: null };
    } catch (error) {
      console.error("Error fetching launch offer status:", error);
      return { active: false, offerTitle: "Website Launch Offer", itemName: "Complimentary 35g Popcorn", endDate: null };
    }
  },

  // Get all showtimes for a movie at a cinema on a specific date (for showtime switcher)
  getVistaShowtimesForDate: async (filmCode, cinemaId, date) => {
    try {
      const params = new URLSearchParams();
      params.append("filmCode", filmCode);
      if (date) params.append("date", date);
      if (cinemaId) params.append("cinemaId", cinemaId);

      const response = await fetch(
        `${API_BASE_URL}/shows/vista?${params.toString()}`,
      );
      const data = await response.json();
      if (data.success) {
        return Array.isArray(data.data) ? data.data : [];
      }
      throw new Error(data.error || "Failed to fetch showtimes");
    } catch (error) {
      console.error("Error fetching showtimes for switcher:", error);
      return []; // Return empty array so switcher just doesn't show
    }
  },

  initiateRazorpayPayment: async (paymentData) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/payments/razorpay/initiate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(paymentData),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error || data.message || "Failed to initiate Razorpay payment",
        );
      }

      return data.order;
    } catch (error) {
      console.error("Error initiating Razorpay payment:", error);
      throw error;
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Google Login (Continue with Google)
  // ─────────────────────────────────────────────────────────────
  googleLogin: async (idToken) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ idToken }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        const error = new Error(
          (data && (data.error || data.message)) ||
            `Google login failed with status ${response.status}`,
        );
        error.response = data;
        throw error;
      }
      return data.data || {};
    } catch (error) {
      console.error("Error during Google login:", error);
      throw error;
    }
  },

  linkGoogleAccount: async ({ userId, idToken, verifiedMobile }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google/link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId, idToken, verifiedMobile }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        const error = new Error(
          (data && (data.error || data.message)) ||
            `Link failed with status ${response.status}`,
        );
        error.response = data;
        throw error;
      }
      return data.data || {};
    } catch (error) {
      console.error("Error linking Google account:", error);
      throw error;
    }
  },

  getCurrentUser: async (userId) => {
    try {
      const headers = userId ? { "X-User-Id": userId } : {};
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: "GET",
        headers,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        return null;
      }
      return data.data || null;
    } catch (error) {
      console.error("Error fetching current user:", error);
      return null;
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Notify Me (Coming Soon movie alerts)
  // ─────────────────────────────────────────────────────────────
  subscribeToMovieNotifications: async (
    movieId,
    { email, mobile, channels, userId } = {},
  ) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/movies/${encodeURIComponent(movieId)}/notify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, mobile, channels, userId }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        const error = new Error(
          (data && (data.error || data.message)) ||
            `Subscribe failed with status ${response.status}`,
        );
        error.response = data;
        throw error;
      }
      return data.data || {};
    } catch (error) {
      console.error("Error subscribing to movie notifications:", error);
      throw error;
    }
  },

  unsubscribeFromMovieNotifications: async (
    movieId,
    { email, mobile, userId } = {},
  ) => {
    try {
      const params = new URLSearchParams();
      if (email) params.append("email", email);
      if (mobile) params.append("mobile", mobile);
      if (userId) params.append("userId", userId);
      const url = `${API_BASE_URL}/movies/${encodeURIComponent(movieId)}/notify${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, mobile, userId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        const error = new Error(
          (data && (data.error || data.message)) ||
            `Unsubscribe failed with status ${response.status}`,
        );
        error.response = data;
        throw error;
      }
      return data.data || {};
    } catch (error) {
      console.error("Error unsubscribing from movie notifications:", error);
      throw error;
    }
  },

  getMovieNotificationStatus: async (
    movieId,
    { email, mobile, userId } = {},
  ) => {
    try {
      const params = new URLSearchParams();
      if (email) params.append("email", email);
      if (mobile) params.append("mobile", mobile);
      if (userId) params.append("userId", userId);
      const url = `${API_BASE_URL}/movies/${encodeURIComponent(
        movieId,
      )}/notify/status${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      const response = await fetch(url, {
        method: "GET",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        // Non-fatal — treat as not subscribed
        console.warn(
          "Notify Me status check failed, defaulting to not subscribed",
          data,
        );
        return { subscribed: false, activeCount: 0 };
      }
      return data.data || { subscribed: false, activeCount: 0 };
    } catch (error) {
      console.error("Error checking movie notification status:", error);
      return { subscribed: false, activeCount: 0 };
    }
  },
};

export default api;
